import {
  fetchRegionFAQ,
  fetchRegionFeatured, fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortListGuide,
  fetchRegionResortsByStatus,
  fetchRegionResortTopRating,
} from 'api/region/static';
import {
  fetchResortDescriptionTabs,
  fetchResortSnowReport,
} from 'api/resort/static';
import {
  IRegionFAQ,
  IRegionFeaturedResort, IRegionShort,
} from 'api/region/types';
import { AxiosResponse } from 'axios';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IRegionLayout } from 'page-components/region/_layout';

import React, {
  useRef, useState, useLayoutEffect,
} from 'react';

import { getRegionSlug } from 'util/memcache';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { IResortTopRated, ResortTopRated, IFeaturedResort } from 'api/home/types';
import {
  IRelatedNews, IResortPageSnowInfo, IResortPathInfo, IDescriptionsResort, ResortStatus,
} from 'api/resort/types';
import getDailyFeaturedResortIndex from 'util/getDailyFeaturedResortIndex';

import { BestResorts } from 'components/home/BestResorts';
import { FeaturedResort } from 'components/home/FeaturedResort';
import { AllResorts } from 'components/region/AllResorts';
import { RegionFAQ } from 'components/region/RegionFAQ';
import { RegularAd } from 'googlead/components/RegularAd';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BlockContent } from 'components/blocks/BlockContent';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { LdJsonRegionOverview } from 'components/LdJson';
import { isMainLocale } from 'util/i18nFlags';
import { Hero400Error } from 'components/400';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionGuidePageProps = {
  featured: IRegionFeaturedResort,
  featuredResortByIndex: IResortPageSnowInfo | null,
  regionResortTopRatingOverall: Array<IResortTopRated>,
  regionResortListGuide: Array<IResortPathInfo>,
  regionFAQ: IRegionFAQ,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  featuredResortDescriptionTabs: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-ski-resorts-page';
export const TEMPLATE_ID = '3.1.1';
export const TEMPLATE_NAME = 'Region Guide Page';

export default function RegionGuidePage({
  featured,
  featuredResortByIndex,
  regionResortTopRatingOverall, pathInfo, regionResortListGuide, regionFAQ, numResorts,
  relatedNews, featuredResortDescriptionTabs,
}: RegionGuidePageProps) {
  const refScroll = useRef<HTMLDivElement>(null);
  const { t } = useOTSTranslation('common');

  const m = useMustacheTranslation('common');
  const { t: seoT } = useOTSTranslation('seo');

  const showLodgingWidget = isMainLocale();
  const featuredRef = useRef<HTMLDivElement>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);

  if (!regionFAQ || !regionResortListGuide || !regionResortTopRatingOverall) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.resortGuide.logicSeoTitle', { region: pathInfo.title })
    : t('region.resortGuide.seoTitle', { region: pathInfo.title });
  const seoText = pathInfo.isLogical ? m('region.resortGuide.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  }) : m('region.resortGuide.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  });

  const regionSeoDescription = seoT('MetaRegion.profile.description', { region: pathInfo.title });

  const featuredResort: IFeaturedResort | null = featuredResortByIndex ? {
    uuid: featuredResortByIndex.uuid,
    title: featuredResortByIndex.title,
    region: featuredResortByIndex.region,
    image: featuredResortByIndex.image,
    smallImage: featuredResortByIndex.smallImage ?? '',
    video: featuredResortByIndex.video ?? null,
    website: featuredResortByIndex.website ?? null,
    phone: featuredResortByIndex.phone ?? null,
    isPartner: featuredResortByIndex.isPartner,
    twitter: featuredResortByIndex.twitter ?? null,
    liftsUrl: featuredResortByIndex.liftsUrl ?? null,
    rentalUrl: featuredResortByIndex.rentalUrl ?? null,
    lessonsUrl: featuredResortByIndex.lessonsUrl ?? null,
    socialMediaAccounts: featuredResortByIndex.socialMediaAccounts ?? [],
  } : null;

  useLayoutEffect(() => {
    if (featuredRef.current) {
      const section = featuredRef.current.querySelector('section');
      const marginBottom = section
        ? parseInt(getComputedStyle(section).marginBottom, 10) || 0
        : 0;
      setSpacerHeight(featuredRef.current.offsetHeight + marginBottom);
    }
  }, [featured]);

  return (
    <>
      <Main>
        {featuredResort && (
          <div ref={featuredRef} className="mb-0">
            <FeaturedResort
              resort={featuredResort}
              regionResorts={regionResortListGuide}
              resortDescriptionTabs={featuredResortDescriptionTabs ?? undefined}
              isOnRegionSkiResorts
              utmCampaign="region_widget"
            />
          </div>
        )}
        <BestResorts
          resortTopRatingOverall={regionResortTopRatingOverall}
          regionUUID={pathInfo.uuid}
          regionTitle={pathInfo.title}
          isFullWidth
        />
        <AiAssistantWidget
          templateId={TEMPLATE_ID}
          name={pathInfo.title}
          margin={{ marginTop: '-0.5rem', marginBottom: '1.75rem' }}
        />
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        <div className="md-hide m-0">
          <AllResorts
            regionResortListGuide={regionResortListGuide}
          />
        </div>
        {showLodgingWidget && (
          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />
        )}
        {regionFAQ && (
          <RegionFAQ
            regionTitle={pathInfo.title}
            regionFAQ={regionFAQ}
            refScroll={refScroll}
            placeholders={{
              region: pathInfo.title,
              NUMRESORTS: String(numResorts),
              numresorts: String(numResorts),
            }}
          />
        )}
        <div className="md-show m-0">
          <AllResorts
            regionResortListGuide={regionResortListGuide}
          />
        </div>
        <RegularAd isFirst />
      </Main>
      <Side>
        {/* Spacer to align sidebar with BestResorts section */}
        {spacerHeight > 0 && (
          <div style={{ height: `${spacerHeight}px` }} className="md-hide" />
        )}
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} />
      </Side>
      <LdJsonRegionOverview
        region={pathInfo}
        resorts={regionResortListGuide}
        topRatedResorts={regionResortTopRatingOverall}
        numResorts={numResorts}
        description={regionSeoDescription}
      />
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionGuidePageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  const partnerResortsResponse = await fetchRegionResortsByStatus(
    uuid,
    ResortStatus.partner,
    1,
    100,
  );
  const partnerResorts = partnerResortsResponse?.data ?? [];

  let featuredResortByIndex: IResortPageSnowInfo | null = null;
  if (partnerResorts.length === 1) {
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[0].uuid);
  } else if (partnerResorts.length > 1) {
    const dailyIndex = getDailyFeaturedResortIndex(partnerResorts.length);
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[dailyIndex].uuid);
  }

  const regionResortTopRatingOverall = await fetchRegionResortTopRating(uuid, ResortTopRated.overall);
  const regionResortListGuide = await fetchRegionResortListGuide(uuid);
  const regionFAQ = await fetchRegionFAQ(uuid);

  const featuredResortDescriptionTabs = featuredResortByIndex?.uuid
    ? await fetchResortDescriptionTabs(featuredResortByIndex.uuid.toString())
    : null;

  return {
    type: 'region',
    route: 'ski-resorts',
    featured,
    featuredResortByIndex,
    regionResortTopRatingOverall,
    regionResortListGuide,
    regionFAQ,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),
    featuredResortDescriptionTabs,

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.TRIP_PLANNING,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.OVERVIEW,
    },
    adTKey: 'metaregion travel',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.resortGuide.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.profile.description',
      title: 'MetaRegion.profile.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionGuidePageStaticProps = wrapStaticProps(fetchProps, false);

export const getRegionGuidePageServerSideProps = wrapServerSideProps(fetchProps, false);
